<template>
  <div class="container mt-4 mb-5">
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border"></div>
    </div>
    <div v-else>
      <div class="row">
        <!-- Left: status summary -->
        <div class="col-md-4">
          <div class="card hoverClick mb-4">
            <div class="card-body text-center" @click="statusFilter = 1; fetchByStatus(1); scrollToList()">
              <div class="circle-emoji mb-3"><span>🚀</span></div>
              <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow" style="line-height: 3rem;">{{
                supportTotals.opened }}</h1>
              <p class="text-muted">Opened tickets</p>
            </div>
          </div>
          <div class="card hoverClick mb-4">
            <div class="card-body text-center" @click="statusFilter = 2; fetchByStatus(2); scrollToList()">
              <div class="circle-emoji mb-3"><span>🧰</span></div>
              <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow" style="line-height: 3rem;">{{
                supportTotals.repair }}</h1>
              <p class="text-muted">In repair tickets</p>
            </div>
          </div>
          <div class="card hoverClick mb-4">
            <div class="card-body text-center" @click="statusFilter = 3; fetchByStatus(3); scrollToList()">
              <div class="circle-emoji mb-3"><span>✅</span></div>
              <h1 class="card-title mb-3 fw-bold display-2 circle-emoji-shadow" style="line-height: 3rem;">{{
                supportTotals.closed }}</h1>
              <p class="text-muted">Closed tickets</p>
            </div>
          </div>
        </div>

        <!-- Right: create form -->
        <div class="col-md-8">
          <div class="card mb-4">
            <div class="card-body">
              <h5 class="card-title fw-bold mb-4">New Support Call</h5>
              <form @submit.prevent="registerSupport" class="mt-2">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Date of Support Call</label>
                    <input type="datetime-local" v-model="supportForm.workDate" class="form-control" required />
                  </div>

                  <div class="col-md-12">
                    <label class="form-label">Tech/Employee</label>
                    <multiselect v-model="supportForm.tech" :options="techStaff" :searchable="true"
                      :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                      placeholder="Select or search tech/employee" label="displayName" track-by="uid"
                      :allow-empty="false">
                      <template #option="props">
                        <span v-if="props.option.displayName || props.option.name">
                          {{ props.option.displayName || props.option.name }}
                          <span v-if="props.option.email" class="text-muted" style="font-size:0.9em;">({{
                            props.option.email }})</span>
                        </span>
                      </template>
                      <template #singleLabel="props">
                        <span v-if="props.option.displayName || props.option.name">
                          {{ props.option.displayName || props.option.name }}
                          <span v-if="props.option.email" class="text-muted" style="font-size:0.9em;">({{
                            props.option.email }})</span>
                        </span>
                      </template>
                    </multiselect>
                  </div>

                  <div class="col-md-12">
                    <div class="form-group mb-3">
                      <label>Company</label>
                      <multiselect v-model="selectedCompany" :options="companies" :searchable="true"
                        :loading="isLoading" :internal-search="false" :clear-on-select="true" :close-on-select="true"
                        :options-limit="10" :min-chars="2" label="company" track-by="id" placeholder="Search company..."
                        @search-change="asyncFindCompany" />
                    </div>
                  </div>

                  <div class="col-md-12 filter-row mb-3">
                    <div class="form-group mb-3">
                      <label>Related Job Name</label>
                      <p>You can select a related job name from the list below:</p>
                      <multiselect v-model="selectedJobName" :options="jobNames" :searchable="true" :loading="isLoading"
                        :internal-search="false" :clear-on-select="true" :close-on-select="true" :options-limit="10"
                        :min-chars="2" label="jobName" track-by="id" placeholder="Search job name..."
                        @search-change="asyncFindJobName" />
                    </div>
                    <div class="text-center my-3">
                      <span class="separator">
                        <span class="separatorChild">or</span>
                      </span>
                    </div>
                    <div class="form-group mb-3">
                      <label>Job Name</label>
                      <p>Or you can use the input field below if there are not related jobs:</p>
                      <input type="text" class="form-control" v-model.trim="jobName" placeholder="Job name"
                        @focus="selectedJobName = null; jobNames = []" @input="selectedJobName = null; jobNames = []" />
                    </div>
                    <div class="form-group mb-3">
                      <p v-if="selectedJobName" class="text-muted mt-3">
                        <strong>Selected job:</strong> {{ selectedJobName.jobName }}
                      </p>
                    </div>
                  </div>

                  <div class="col-12">
                    <label class="form-label">Issue Reported</label>
                    <textarea v-model.trim="supportForm.issueReported" class="form-control" rows="3" maxlength="200"
                      required></textarea>
                  </div>

                  <div class="col-12">
                    <label class="form-label">Support Time</label>
                    <div class="row g-2 align-items-end">
                      <div class="col-md-5">
                        <input type="datetime-local" v-model="dateInput.startTime" class="form-control"
                          placeholder="Start Time" />
                      </div>
                      <div class="col-md-5">
                        <input type="datetime-local" v-model="dateInput.stopTime" class="form-control"
                          placeholder="Stop Time" />
                      </div>
                      <div class="col-md-2">
                        <button type="button" class="btn btn-outline-primary btn-sm w-100" @click="addDate">Add
                          Date</button>
                      </div>
                    </div>
                    <ul class="list-group mt-2" v-if="supportForm.dates.length">
                      <li class="list-group-item d-flex justify-content-between align-items-center"
                        v-for="(d, idx) in supportForm.dates" :key="idx">
                        <p class="mb-0">{{ new Date(d.startTime).toLocaleString() }} - {{ new
                          Date(d.stopTime).toLocaleString() }} <span class="text-muted">(⏲️ {{ d.totalHours.toFixed(2)
                            }} hr)</span></p>
                        <button type="button" class="btn btn-sm btn-danger" @click="removeDate(idx)">Remove</button>
                      </li>
                    </ul>
                  </div>

                  <div class="col-12">
                    <label class="form-label">Description of Work Done</label>
                    <textarea v-model.trim="supportForm.description" class="form-control" rows="6"
                      maxlength="1000"></textarea>
                  </div>

                  <div class="col-12 mt-3">
                    <button type="submit" class="btn btn-primary w-100">Save Support Call</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- List and filters -->
      <div class="row">
        <div class="col-md-12">
          <div class="card mt-5" ref="listTable">
            <div class="card-body">
              <h5 class="card-title mb-3">📱 Support Calls</h5>

              <div class="row mb-3 mt-3 filter-row">
                <div class="col-md-12">
                  <p class="fw-bold">Filter</p>
                </div>
                <div class="col-md-12">
                  <label>Company</label>
                  <multiselect v-model="filterCompany" :options="filterCompanies" :searchable="true"
                    :internal-search="false" :clear-on-select="true" :close-on-select="true" :options-limit="10"
                    :min-chars="2" label="company" track-by="id" placeholder="Search company..."
                    @search-change="asyncFindFilterCompany" />

                  <label class="mt-3">Related Job Name</label>
                  <multiselect v-model="jobNameFilter" :options="jobNameFilterOptions" :searchable="true"
                    :loading="jobNameFilterIsLoading" :internal-search="false" :clear-on-select="true"
                    :close-on-select="true" :options-limit="10" :min-chars="2" label="jobName" track-by="id"
                    placeholder="Search job name..." @search-change="asyncFindJobNameFilter" />

                  <div class="text-center my-3">
                    <span class="separator">
                      <span class="separatorChild">or</span>
                    </span>
                  </div>
                  <label>Job Name</label>
                  <input type="text" class="form-control" v-model.trim="searchJob" placeholder="Type job name..."
                    @focus="jobNameFilter = null; jobNameFilterOptions = []"
                    @input="jobNameFilter = null; jobNameFilterOptions = []" />
                  <p v-if="jobNameFilter" class="text-muted mt-2">
                    <strong>Selected job:</strong> {{ jobNameFilter.jobName }}
                  </p>

                  <label class="mt-3">Tech/Employee</label>
                  <multiselect v-model="techFilter" :options="techStaff" :searchable="true" :close-on-select="true"
                    :clear-on-select="true" :preserve-search="true" placeholder="Select or search tech/employee"
                    label="displayName" track-by="uid" :allow-empty="true">
                    <template #option="props">
                      <span v-if="props.option.displayName || props.option.name">
                        {{ props.option.displayName || props.option.name }}
                        <span v-if="props.option.email" class="text-muted" style="font-size:0.9em;">({{
                          props.option.email }})</span>
                      </span>
                    </template>
                    <template #singleLabel="props">
                      <span v-if="props.option.displayName || props.option.name">
                        {{ props.option.displayName || props.option.name }}
                        <span v-if="props.option.email" class="text-muted" style="font-size:0.9em;">({{
                          props.option.email }})</span>
                      </span>
                    </template>
                  </multiselect>

                  <div class="d-flex gap-2 mt-3">
                    <button class="btn btn-primary" @click="search(1)">Search</button>
                  </div>
                </div>
                <hr class="mt-3">

                <div class="col-md-6" ref="mainTable">
                  <p class="fw-bold">Fitler by status</p>
                  <div class="btn-group w-100 mb-3" role="group" aria-label="Status filter">
                    <button type="button" class="btn" :class="statusFilter === 0 ? 'btn-primary' : 'btn-outline-dark'"
                      @click="statusFilter = 0; fetchByStatus(0)">
                      All
                    </button>
                    <button type="button" class="btn"
                      :class="{'btn-primary': statusFilter === 1, 'btn-outline-dark': statusFilter !== 1}"
                      @click="statusFilter = 1; fetchByStatus(1)">
                      🚀 Opened
                    </button>
                    <button type="button" class="btn"
                      :class="{'btn-primary': statusFilter === 2, 'btn-outline-dark': statusFilter !== 2}"
                      @click="statusFilter = 2; fetchByStatus(2)">
                      🧰 In Repair
                    </button>
                    <button type="button" class="btn"
                      :class="{'btn-primary': statusFilter === 3, 'btn-outline-dark': statusFilter !== 3}"
                      @click="statusFilter = 3; fetchByStatus(3)">
                      ✅ Closed
                    </button>
                  </div>
                </div>
                <div class="col-md-3">

                </div>
                <div class="col-md-3"></div>
                <div class="col-md-12 text-center mt-5">
                  <p class="serial-number" @click="resetFilters">🔄 Reset filters</p>
                </div>

                <!-- Print by Job Name section -->
                <div class="col-md-12 mt-4">
                  <hr />
                  <p class="fw-bold">Print Job Name summary</p>
                  <label>Company</label>
                  <multiselect
                    v-model="reportCompany"
                    :options="reportCompanies"
                    :searchable="true"
                    :internal-search="false"
                    :clear-on-select="true"
                    :close-on-select="true"
                    :options-limit="10"
                    :min-chars="2"
                    label="company"
                    track-by="id"
                    placeholder="Search company..."
                    @search-change="asyncFindReportCompany"
                  />

                  <label class="mt-3">Related Job Name</label>
                  <multiselect
                    v-model="reportJob"
                    :options="reportJobOptions"
                    :searchable="true"
                    :loading="reportJobIsLoading"
                    :internal-search="false"
                    :clear-on-select="true"
                    :close-on-select="true"
                    :options-limit="10"
                    :min-chars="2"
                    label="jobName"
                    track-by="id"
                    placeholder="Search job name..."
                    @search-change="asyncFindReportJob"
                  />

                  <div class="row g-2 mt-3">
                    <div class="col-md-6">
                      <label class="form-label">Start date</label>
                      <input type="datetime-local" class="form-control" v-model="reportStartDate" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">End date</label>
                      <input type="datetime-local" class="form-control" v-model="reportEndDate" />
                    </div>
                  </div>
                  <div class="mt-3 d-flex gap-2">
                    <button class="btn btn-outline-primary" @click="previewJobReport">Preview summary</button>
                    <button class="btn btn-primary" @click="printJobReport">Print summary</button>
                    <button class="btn btn-outline-secondary" @click="resetReport">Clear</button>
                  </div>

                  <!-- Inline preview -->
                  <div class="card mt-3" v-if="selectedReport">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Summary</h6>
                      <div id="preview-report">
                        <table class="table table-bordered fSmall report-table">
                          <tbody>
                            <tr>
                              <th>Job Name</th>
                              <td>{{ selectedReport.jobName }}</td>
                            </tr>
                            <tr>
                              <th>Range Start</th>
                              <td>{{ formatDate(selectedReport.rangeStart) }}</td>
                            </tr>
                            <tr>
                              <th>Range End</th>
                              <td>{{ formatDate(selectedReport.rangeEnd) }}</td>
                            </tr>
                            <tr>
                              <th>Total hours</th>
                              <td>{{ Number(selectedReport.totalHours || 0).toFixed(2) }}</td>
                            </tr>
                          </tbody>
                        </table>

                        <h6 class="mt-3">Details</h6>
                        <div class="table-responsive" v-if="selectedReport.remoteHistory && selectedReport.remoteHistory.length">
                          <table class="table table-sm table-striped">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>From</th>
                                <th>To</th>
                                <th>Total hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(r, idx) in selectedReport.remoteHistory" :key="r.id">
                                <td>{{ idx + 1 }}</td>
                                <td>{{ r.startDate }}</td>
                                <td>{{ r.endDate }}</td>
                                <td>{{ Number(r.totalHours || 0).toFixed(2) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p class="text-muted" v-else>No details in selected range.</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>


              <div class="col-md-12">
                <div class="d-flex justify-content-end mt-2">
                  <v-pagination v-model.number="currentPage" :page-count="totalPages" @input="pageChanged" />
                </div>
              </div>
              
               <div class="col-md-12 d-flex justify-content-between align-items-center mt-2 mb-3">
                 <p class="mb-0">Total records: {{ (supportTotals.opened || 0) + (supportTotals.repair || 0) + (supportTotals.closed || 0) }}</p>
                 <p class="mb-0">Pages: {{ totalPages }}</p>
               </div>
      

              <vs-table>
                <template #thead>
                  <vs-tr>
                    <vs-th>Call ID</vs-th>
                    <vs-th>Date</vs-th>
                    <vs-th>Tech</vs-th>
                    <vs-th>Job</vs-th>
                    <vs-th>Status</vs-th>
                    <vs-th style="width: 160px;">Actions</vs-th>
                  </vs-tr>
                </template>
                <template #tbody>
                  <vs-tr v-for="call in calls" :key="call.id">
                    <vs-td>
                      <p class="serial-number mb-2" @click.prevent="openEdit(call)">#{{ call.id }}</p>
                    </vs-td>
                    <vs-td>
                      <p class="mb-2">{{ call.workDate ? call.workDate.substring(0,16).replace('T',' ') : '' }}</p>
                    </vs-td>
                    <vs-td>
                      <p class="mb-2" :title="call.tech">{{ call.tech }}</p>
                    </vs-td>
                    <vs-td>
                      <p class="mb-2">{{ call.jobName }}</p>
                    </vs-td>
                    <vs-td>
                      <template v-if="call.status==1">
                        <p class="mb-2">🚀 opened</p>
                      </template>
                      <template v-else-if="call.status==2">
                        <p class="mb-2">🧰 in repair</p>
                      </template>
                      <template v-else-if="call.status==3">
                        <p class="mb-2">✅ closed</p>
                      </template>
                      <template v-else>
                        <p class="mb-2">Unknown</p>
                      </template>
                    </vs-td>
                    <vs-td class="actions-col">
                      <p class="serial-number me-3 mb-2" @click.prevent="printSupport(call)">🖨️ Print</p>
                      <p class="serial-number me-3 mb-2" @click.prevent="openEdit(call)">✏️ Edit</p>
                      <p class="serial-number text-danger" @click.prevent="deleteConfirm(call)">❌ Delete</p>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>

              <div class="d-flex justify-content-center align-items-center mt-3">
                <v-pagination v-model.number="currentPage" :page-count="totalPages" @input="pageChanged" />
              </div>
              <div class="d-flex justify-content-between align-items-center mt-3">
                <p class="mb-0">Total records: {{ (supportTotals.opened || 0) + (supportTotals.repair || 0) + (supportTotals.closed || 0) }}</p>
                <p class="mb-0">Pages: {{ totalPages }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid" id="print-area" v-if="selectedSupport2">
      <div class="row">
        <div class="col-lg-12">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <div>
              <img src="@/assets/logo-nav.png" width="180" />
            </div>
            <div class="text-center">
              <img :src="qrData" alt="Generated QR" style="height: 150px; border-style: solid; border-width: 0.6rem;" />
              <div class="fw-bold" style="font-size: 1.5rem;">Support Call #{{ selectedSupport2.id }}</div>
            </div>
          </div>
          <h5>Support Call</h5>
          <table class="table table-bordered fSmall w-100">
            <tbody>
              <tr>
                <th>Date</th>
                <td>{{ selectedSupport2.workDate ? new Date(selectedSupport2.workDate).toLocaleString() : '' }}</td>
              </tr>
              <tr>
                <th>Job Name</th>
                <td>{{ selectedSupport2.jobName }}</td>
              </tr>
              <tr>
                <th>Tech</th>
                <td>{{ selectedSupport2.tech }}</td>
              </tr>
              <tr v-if="selectedSupport2.companyName">
                <th>Company</th>
                <td>{{ selectedSupport2.companyName }}</td>
              </tr>
              <tr>
                <th>Issue Reported</th>
                <td>{{ selectedSupport2.issueReported }}</td>
              </tr>
              <tr>
                <th>Description</th>
                <td>{{ selectedSupport2.description }}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>
                  <template v-if="selectedSupport2.status==1">🚀 opened</template>
                  <template v-else-if="selectedSupport2.status==2">🧰 in repair</template>
                  <template v-else-if="selectedSupport2.status==3">✅ closed</template>
                  <template v-else>Unknown</template>
                </td>
              </tr>
              <tr>
                <th>Total hours</th>
                <td>
                  <div v-if="selectedSupport2 && selectedSupport2.history && selectedSupport2.history.length" class="mt-1">
                    {{ computeTotalHours(selectedSupport2.history) }} 
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <h6 class="mt-4" v-if="selectedSupport2.history && selectedSupport2.history.length">Details</h6>
          <table class="table table-sm table-striped" v-if="selectedSupport2.history && selectedSupport2.history.length">
            <thead>
              <tr>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Total hours</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(h, idx) in selectedSupport2.history" :key="h.id">
                <td>-Call time</td>
                <td>{{ h.start ? new Date(h.start).toLocaleString() : '' }}</td>
                <td>{{ h.end ? new Date(h.end).toLocaleString() : '' }}</td>
                <td>{{ Number(h.hours).toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Print area: Job Name Summary -->
    <div class="container-fluid" id="print-area-report" v-if="selectedReport">
      <div class="row">
        <div class="col-lg-12">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <div>
              <img src="@/assets/logo-nav.png" width="180" />
            </div>
            <div class="text-center">
              <img :src="qrDataReport" alt="Generated QR" style="height: 150px; border-style: solid; border-width: 0.6rem;" />
              <div class="fw-bold" style="font-size: 1.5rem;">Support Calls by Job</div>
            </div>
          </div>
          <h5>Summary</h5>
          <table class="table table-bordered fSmall">
            <tbody>
              <tr>
                <th>Job Name</th>
                <td>{{ selectedReport.jobName }}</td>
              </tr>
              <tr>
                <th>Range Start</th>
                <td>{{ formatDate(selectedReport.rangeStart) }}</td>
              </tr>
              <tr>
                <th>Range End</th>
                <td>{{ formatDate(selectedReport.rangeEnd) }}</td>
              </tr>
              <tr>
                <th>Total hours</th>
                <td>{{ Number(selectedReport.totalHours || 0).toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
          <h6 class="mt-4" v-if="selectedReport.remoteHistory && selectedReport.remoteHistory.length">Details</h6>
          <table class="table table-sm table-striped" v-if="selectedReport.remoteHistory && selectedReport.remoteHistory.length">
            <thead>
              <tr>
                <th>#</th>
                <th>From</th>
                <th>To</th>
                <th>Total hours</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in selectedReport.remoteHistory" :key="r.id">
                <td>{{ idx + 1 }}</td>
                <td>{{ r.startDate }}</td>
                <td>{{ r.endDate }}</td>
                <td>{{ Number(r.totalHours || 0).toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div class="modal fade" id="supportEditModal" tabindex="-1" aria-labelledby="supportEditModalLabel"
      aria-hidden="true" ref="supportEditModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="supportEditModalLabel">Edit Support Call</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedCall">
              <div class="card p-3 mt-3">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Tech/Employee</label>
                    <input type="text" class="form-control" v-model.trim="editForm.tech" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Job Name</label>
                    <input type="text" class="form-control" v-model.trim="editForm.jobName" />
                  </div>
                  <div class="col-md-12">
                    <label class="form-label">Issue Reported</label>
                    <textarea class="form-control" rows="3" maxlength="200"
                      v-model.trim="editForm.issueReported"></textarea>
                  </div>
                  <div class="col-md-12">
                    <label class="form-label">Description of Work Done</label>
                    <textarea class="form-control" rows="6" maxlength="1000"
                      v-model.trim="editForm.description"></textarea>
                  </div>
                  <div class="col-md-12">
                    <label class="form-label">Status</label>
                    <div>
                      <div class="form-check form-check-inline">
                        <input class="form-check-input" type="radio" v-model.number="editForm.status" :value="1"
                          id="editStatusOpened">
                        <label class="form-check-label" for="editStatusOpened">Opened</label>
                      </div>
                      <div class="form-check form-check-inline">
                        <input class="form-check-input" type="radio" v-model.number="editForm.status" :value="2"
                          id="editStatusInRepair">
                        <label class="form-check-label" for="editStatusInRepair">In repair</label>
                      </div>
                      <div class="form-check form-check-inline">
                        <input class="form-check-input" type="radio" v-model.number="editForm.status" :value="3"
                          id="editStatusClosed">
                        <label class="form-check-label" for="editStatusClosed">Closed</label>
                      </div>
                    </div>
                  </div>
                  <div class="text-end mt-3">
                    <button class="btn btn-success btn-sm" @click="updateSupportCall">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>

              <hr class="my-4" />
              <div class="card p-3 mt-3">
                <h6 class="fw-bold">Tracking times</h6>
                <div class="row g-3 mt-2 mb-5">
                  <div class="col-md-5">
                    <label class="form-label">Start time</label>
                    <input type="datetime-local" class="form-control" v-model="moreTime.start" />
                  </div>
                  <div class="col-md-5">
                    <label class="form-label">Stop time</label>
                    <input type="datetime-local" class="form-control" v-model="moreTime.stop" />
                  </div>
                  <div class="col-md-2 d-flex align-items-end">
                    <button class="btn btn-outline-primary w-100" @click="addMoreTime">Add Time</button>
                  </div>
                </div>
                <div class="table-responsive" v-if="timeHistory.length">
                  <table class="table table-sm table-striped align-middle">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Hours</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(t, idx) in timeHistory" :key="t.id">
                        <td>{{ idx + 1 }}</td>
                        <td>{{ t.start ? new Date(t.start).toLocaleString() : '' }}</td>
                        <td>{{ t.end ? new Date(t.end).toLocaleString() : '' }}</td>
                        <td>{{ Number(t.hours).toFixed(2) }}</td>
                        <td>
                          <button class="btn btn-sm btn-danger" @click="deleteTime(t)">Delete</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p class="text-muted" v-else>No time records.</p>

             

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div class="modal fade" id="deleteSupportModal" tabindex="-1" aria-labelledby="deleteSupportModalLabel"
      aria-hidden="true" ref="deleteSupportModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="deleteSupportModalLabel">⚠️ Confirm Delete</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this support call?</p>
            <p v-if="selectedCall"><strong>Call ID:</strong> #{{ selectedCall.id }}</p>
            <p v-if="selectedCall"><strong>Job Name:</strong> {{ selectedCall.jobName }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
  import axios from 'axios'
  import vPagination from 'vue-plain-pagination'
  import Multiselect from 'vue-multiselect'
  import printJS from 'print-js'
  import QRCode from 'qrcode'
  var config = require('@/config/api.json')
  const API = config.api
  const API2 = config.apiManager; 

  export default {
    name: 'SuppotCalls',
    components: { vPagination, Multiselect },
    data() {
      return {
        loading: true,
        statusFilter: 0,
        supportTotals: { opened: 0, repair: 0, closed: 0 },

        supportForm: {
          workDate: '',
          tech: '', // object from multiselect or string (email)
          issueReported: '',
          description: '',
          dates: [] // { startTime, stopTime, totalHours }
        },
        dateInput: { startTime: '', stopTime: '' },

        calls: [],
        currentPage: 1,
        totalPages: 1,

        searchJob: '',
        searchTech: '',

        selectedCall: null,
        editForm: { id: null, tech: '', jobName: '', status: 1, issueReported: '', description: '' },
        timeHistory: [],
        moreTime: { start: '', stop: '' },

        techStaff: [],

        // Company and JobName (like Inventory)
        companies: [],
        selectedCompany: null,
        selectedJobName: null,
        jobNames: [],
        isLoading: false,
        jobName: '',

        // Filters
        filterCompany: null,
        filterCompanies: [],
        jobNameFilter: null,
        jobNameFilterOptions: [],
        jobNameFilterIsLoading: false,
        techFilter: null,
        // Print helpers
        selectedSupport2: null,
        qrData: null,
        // Report (by job) state
        reportCompany: null,
        reportCompanies: [],
        reportJob: null,
        reportJobOptions: [],
        reportJobIsLoading: false,
        reportStartDate: '',
        reportEndDate: '',
        selectedReport: null,
        qrDataReport: null
      }
    },
    async mounted() {
      await Promise.all([this.fetchTotals(), this.fetchTechStaff()])
      await this.fetchCalls(1)
      this.loading = false
    },
    methods: {
      scrollToList() {
        const ref = this.$refs.listTable
        const el = ref && ref.$el ? ref.$el : ref
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth' })
        }
      },
      async fetchTechStaff() {
        try {
          const res = await axios.get(`${API}/get-techStaff`)
          this.techStaff = res.data || []
        } catch (e) {
          this.techStaff = []
        }
      },
      async fetchTotals() {
        try {
          const res = await axios.get(`${API}/supportcall-totals`)
          this.supportTotals = res.data || { opened: 0, repair: 0, closed: 0 }
        } catch (e) {
          this.supportTotals = { opened: 0, repair: 0, closed: 0 }
        }
      },
      async createQr(id) {
        try {
          const value = `${API2}/print-support-call/${encodeURIComponent(String(id))}`
          this.qrData = await QRCode.toDataURL(value)
        } catch (err) {
          console.error('Error generating QR:', err)
        }
      },
      async printSupport(call) {
        try {
          this.selectedSupport2 = { ...call }
          try {
            const res = await axios.get(`${API}/get-supportcall-details/${call.id}`)
            this.selectedSupport2.history = Array.isArray(res.data) ? res.data : []
          } catch (e) {
            this.selectedSupport2.history = []
          }
          await this.createQr(call.id)
          // Force reactivity to ensure DOM updates for print content
          await this.$nextTick()
          try { await this.callLog(this.storedUser?.email || '--', 'print', 'remoteCalls', `Print support call ID ${call.id}`) } catch (e) {}
          this.$nextTick(() => {
            printJS({
              printable: 'print-area',
              type: 'html',
              targetStyles: ['*'],
              scanStyles: true,
              onPrintDialogClose: () => {
                // Clear and re-hydrate to avoid stale state when canceling print
                this.selectedSupport2 = null
                this.qrData = null
              }
            })
          })
          setTimeout(() => {
            this.selectedSupport2 = null
            this.qrData = null
          }, 1000)
        } catch (e) {
          this.notify('error', 'Error printing support call')
        }
      },
      async buildReportQr(jobName, startDate, endDate) {
        try {
          const url = `${API2}/printRemoteCalls/${encodeURIComponent(jobName)}/${encodeURIComponent(startDate)}/${encodeURIComponent(endDate)}`
          this.qrDataReport = await QRCode.toDataURL(url)
        } catch (e) {
          this.qrDataReport = null
        }
      },
      async printJobReport() {
        try {
          if (!this.reportCompany || !this.reportCompany.id) {
            this.notify('error', 'Please select a company')
            return
          }
          if (!this.reportJob || !this.reportJob.jobName) {
            this.notify('error', 'Please select a job name')
            return
          }
          if (!this.reportStartDate || !this.reportEndDate) {
            this.notify('error', 'Please select a valid date range')
            return
          }
          if (new Date(this.reportEndDate) < new Date(this.reportStartDate)) {
            this.notify('error', 'Start date cannot be after end date')
            return
          }

          const params = {
            jobName: this.reportJob.jobName,
            startDate: this.reportStartDate,
            endDate: this.reportEndDate
          }
          const res = await axios.get(`${API}/get-by-jobaname-dates`, { params })
          this.selectedReport = res.data || null
          await this.buildReportQr(params.jobName, params.startDate, params.endDate)
          await this.$nextTick()
          printJS({
            printable: 'print-area-report',
            type: 'html',
            targetStyles: ['*'],
            scanStyles: true,
            onPrintDialogClose: () => {
              this.selectedReport = null
              this.qrDataReport = null
            }
          })
        } catch (e) {
          this.notify('error', 'Error printing job summary')
        }
      },
      resetReport() {
        this.reportCompany = null
        this.reportCompanies = []
        this.reportJob = null
        this.reportJobOptions = []
        this.reportStartDate = ''
        this.reportEndDate = ''
        this.selectedReport = null
        this.qrDataReport = null
      },
      async asyncFindReportCompany(query) {
        if (!query || query.length < 2) {
          this.reportCompanies = []
          return
        }
        try {
          const res = await axios.get(`${API}/search-company`, { params: { company: query } })
          this.reportCompanies = res.data
        } catch (e) {
          this.notify('error', 'Error searching companies')
        }
      },
      async asyncFindReportJob(query) {
        if (!this.reportCompany || !this.reportCompany.id) {
          this.reportJobOptions = []
          this.notify('error', 'Please select a company first')
          return
        }
        if (!query || query.length < 2) {
          this.reportJobOptions = []
          return
        }
        this.reportJobIsLoading = true
        try {
          const res = await axios.get(`${API}/search-job`, { params: { job: query, companyId: this.reportCompany.id } })
          this.reportJobOptions = res.data
        } catch (e) {
          this.notify('error', 'Error searching job names')
        } finally {
          this.reportJobIsLoading = false
        }
      },
      async previewJobReport() {
        try {
          if (!this.reportCompany || !this.reportCompany.id) {
            this.notify('error', 'Please select a company')
            return
          }
          if (!this.reportJob || !this.reportJob.jobName) {
            this.notify('error', 'Please select a job name')
            return
          }
          if (!this.reportStartDate || !this.reportEndDate) {
            this.notify('error', 'Please select a valid date range')
            return
          }
          if (new Date(this.reportEndDate) < new Date(this.reportStartDate)) {
            this.notify('error', 'Start date cannot be after end date')
            return
          }
          const params = {
            jobName: this.reportJob.jobName,
            startDate: this.reportStartDate,
            endDate: this.reportEndDate
          }
          const res = await axios.get(`${API}/get-by-jobaname-dates`, { params })
          this.selectedReport = res.data || null
        } catch (e) {
          this.notify('error', 'Error loading summary preview')
        }
      },
      formatDate(d) {
        try {
          const dt = new Date(d)
          if (Number.isNaN(dt.getTime())) return d
          const yyyy = dt.getFullYear()
          const mm = String(dt.getMonth() + 1).padStart(2, '0')
          const dd = String(dt.getDate()).padStart(2, '0')
          const hh = String(dt.getHours()).padStart(2, '0')
          const mi = String(dt.getMinutes()).padStart(2, '0')
          return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
        } catch (e) {
          return d
        }
      },
      computeTotalHours(history) {
        try {
          const total = (history || []).reduce((acc, h) => acc + (Number(h.hours) || 0), 0)
          return total.toFixed(2)
        } catch (e) {
          return '0.00'
        }
      },
      async fetchCalls(page = 1) {
        this.currentPage = page
        try {
          const res = await axios.post(`${API}/get-supportcalls`, { page })
          this.calls = Array.isArray(res.data?.data) ? res.data.data : []
          this.totalPages = res.data?.totalPages || 1
        } catch (e) {
          this.calls = []
          this.totalPages = 1
          this.notify('error', 'Error loading support calls')
        }
      },
      async fetchByStatus(status, page = 1) {
        this.currentPage = page
        try {
          const res = await axios.post(`${API}/filter-supportcalls`, { status, page })
          this.calls = Array.isArray(res.data?.data) ? res.data.data : []
          this.totalPages = res.data?.totalPages || 1
        } catch (e) {
          this.calls = []
          this.totalPages = 1
          this.notify('error', 'Error filtering support calls')
        }
      },
      pageChanged(page) {
        if (this.statusFilter && this.statusFilter !== 0) {
          this.fetchByStatus(this.statusFilter, page)
        } else if (this.jobNameFilter || this.searchJob || this.searchTech) {
          this.search(page)
        } else {
          this.fetchCalls(page)
        }
      },
      resetFilters() {
        this.filterCompany = null
        this.filterCompanies = []
        this.jobNameFilter = null
        this.jobNameFilterOptions = []
        this.searchJob = ''
        this.searchTech = ''
        this.statusFilter = 0
        this.filterCompany = null
        this.filterCompanies = []
        this.jobNameFilter = null
        this.jobNameFilterOptions = []
        this.fetchCalls(1)
      },
      async search(page = 1) {
        this.currentPage = page
        try {
          const payload = {}
          // Resolve job name from the dependent filter first
          if (this.jobNameFilter && this.jobNameFilter.jobName) {
            payload.sJob = this.jobNameFilter.jobName
          } else if (this.searchJob) {
            payload.sJob = this.searchJob
          }
          if (this.techFilter) {
            const t = this.techFilter
            payload.sTech = t.email || t.displayName || t.name || ''
          } else if (this.searchTech) {
            payload.sTech = this.searchTech
          }
          payload.page = page
          const res = await axios.post(`${API}/search-supportcalls`, payload)
          this.calls = Array.isArray(res.data?.data) ? res.data.data : []
          this.totalPages = res.data?.totalPages || 1
        } catch (e) {
          this.notify('error', 'Error searching support calls')
        }
      },

      addDate() {
        if (!this.dateInput.startTime || !this.dateInput.stopTime) return
        const start = new Date(this.dateInput.startTime)
        const stop = new Date(this.dateInput.stopTime)
        if (stop < start) {
          this.notify('error', 'Stop time cannot be before start time.')
          return
        }
        const diffHours = Math.abs(stop.getTime() - start.getTime()) / 3600000
        this.supportForm.dates.push({ startTime: this.dateInput.startTime, stopTime: this.dateInput.stopTime, totalHours: diffHours })
        this.dateInput.startTime = ''
        this.dateInput.stopTime = ''
      },
      removeDate(idx) {
        this.supportForm.dates.splice(idx, 1)
      },

      // Filter remote search helpers
      async asyncFindFilterCompany(query) {
        if (!query || query.length < 2) {
          this.filterCompanies = []
          return
        }
        try {
          const res = await axios.get(`${API}/search-company`, { params: { company: query } })
          this.filterCompanies = res.data
        } catch (e) {
          this.notify('error', 'Error searching companies')
        }
      },
      async asyncFindJobNameFilter(query) {
        if (!this.filterCompany || !this.filterCompany.id) {
          this.jobNameFilterOptions = []
          this.notify('error', 'Please select a company first')
          return
        }
        if (!query || query.length < 2) {
          this.jobNameFilterOptions = []
          return
        }
        this.jobNameFilterIsLoading = true
        try {
          const res = await axios.get(`${API}/search-job`, { params: { job: query, companyId: this.filterCompany.id } })
          this.jobNameFilterOptions = res.data
        } catch (e) {
          this.notify('error', 'Error searching job names')
        } finally {
          this.jobNameFilterIsLoading = false
        }
      },
      async registerSupport() {
        try {
          // Validations similar to Inventory
          if (!this.selectedCompany) {
            this.notify('error', 'Please select a company')
            return
          }

          let realJobName
          let jobId = 0
          if (this.selectedJobName && this.selectedJobName.id) {
            realJobName = this.selectedJobName.jobName
            jobId = this.selectedJobName.id
          } else {
            realJobName = this.jobName
          }

          if (!this.vEmpty(realJobName, 'Job Name')) return

          const payload = {
            workDate: this.supportForm.workDate,
            tech: typeof this.supportForm.tech === 'object' ? (this.supportForm.tech.email || this.supportForm.tech.displayName || '') : this.supportForm.tech,
            jobName: realJobName,
            issueReported: this.supportForm.issueReported,
            description: this.supportForm.description,
            dates: this.supportForm.dates.map(d => ({ startTime: d.startTime, stopTime: d.stopTime, totalHours: d.totalHours })),
            user_id: this.selectedCompany.id,
            companyName: this.selectedCompany.company,
            jobId
          }

          if (!payload.tech) {
            this.notify('error', 'You must select a Tech/Employee.')
            return
          }

          const res = await axios.post(`${API}/create-supportcall`, payload)
          if (res.data && res.data.message === 'success') {
            this.notify('success', 'Support call created successfully!')
            try { await this.callLog(this.storedUser?.email || '--', 'create', 'remoteCalls', `Created support call with ID ${res.data.id}`) } catch (e) { }
            this.supportForm = { workDate: '', tech: '', jobName: '', issueReported: '', description: '', dates: [] }
            // Clear company/job selections
            this.selectedCompany = null
            this.selectedJobName = null
            this.jobNames = []
            this.companies = []
            this.jobName = ''
            await Promise.all([this.fetchCalls(1), this.fetchTotals()])
          } else {
            throw new Error('Create failed')
          }
        } catch (e) {
          this.notify('error', 'Error creating support call')
        }
      },

      // Remote search like Inventory
      async asyncFindCompany(query) {
        if (!query || query.length < 2) {
          this.companies = []
          return
        }
        this.isLoading = true
        try {
          const res = await axios.get(`${API}/search-company`, { params: { company: query } })
          this.companies = res.data
        } catch (e) {
          this.notify('error', 'Error searching companies')
        } finally {
          this.isLoading = false
        }
      },
      async asyncFindJobName(query) {
        if (!this.selectedCompany || !this.selectedCompany.id) {
          this.jobNames = []
          this.notify('error', 'Please select a company first')
          return
        }
        if (!query || query.length < 2) {
          this.jobNames = []
          return
        }
        this.isLoading = true
        try {
          const res = await axios.get(`${API}/search-job`, { params: { job: query, companyId: this.selectedCompany.id } })
          this.jobNames = res.data
        } catch (e) {
          this.notify('error', 'Error searching job names')
        } finally {
          this.isLoading = false
        }
      },

      async openEdit(call) {
        this.selectedCall = call
        this.editForm = { id: call.id, tech: call.tech || '', jobName: call.jobName || '', status: Number(call.status) || 1, issueReported: call.issueReported || '', description: call.description || '' }
        await this.loadTimeHistory(call.id)
        const modal = this.$refs.supportEditModal
        if (modal && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const bsModal = bootstrap.Modal.getOrCreateInstance(modal)
          setTimeout(() => bsModal.show(), 150)
        }
      },
      async loadTimeHistory(id) {
        try {
          const res = await axios.get(`${API}/get-supportcall-details/${id}`)
          this.timeHistory = Array.isArray(res.data) ? res.data : []
        } catch (e) {
          this.timeHistory = []
        }
      },
      async updateSupportCall() {
        try {
          const body = { ...this.editForm }
          const res = await axios.post(`${API}/update-supportcall`, body)
          if (res.data && res.data.message === 'success') {
            this.notify('success', 'Support call updated')
            try { await this.callLog(this.storedUser?.email || '--', 'update', 'remoteCalls', `Updated support call with ID ${this.editForm.id}`) } catch (e) { }
            await Promise.all([this.fetchCalls(this.currentPage), this.fetchTotals()])
          } else {
            throw new Error('Update failed')
          }
        } catch (e) {
          this.notify('error', 'Error updating support call')
        }
      },
      async addMoreTime() {
        if (!this.selectedCall) return
        if (!this.moreTime.start || !this.moreTime.stop) return
        const start = new Date(this.moreTime.start)
        const stop = new Date(this.moreTime.stop)
        if (stop <= start) {
          this.notify('error', 'Stop time must be greater than start time')
          return
        }
        const diffHours = Math.abs(stop.getTime() - start.getTime()) / 3600000
        try {
          const body = { repairId: this.selectedCall.id, start: this.moreTime.start, stop: this.moreTime.stop, totalHours: diffHours }
          const res = await axios.post(`${API}/create-supportcall-time`, body)
          this.timeHistory = Array.isArray(res.data) ? res.data : []
          this.moreTime.start = ''
          this.moreTime.stop = ''
          this.notify('success', 'Time added successfully')
          try { await this.callLog(this.storedUser?.email || '--', 'create', 'remoteHistory', `Added time to support call ID ${this.selectedCall.id}`) } catch (e) { }
        } catch (e) {
          this.notify('error', 'Error adding time')
        }
      },
      async deleteTime(t) {
        try {
          await axios.delete(`${API}/delete-supportcall-time/${t.id}`, { data: { repairId: this.selectedCall.id } })
          await this.loadTimeHistory(this.selectedCall.id)
          this.notify('success', 'Time deleted successfully')
          try { await this.callLog(this.storedUser?.email || '--', 'delete', 'remoteHistory', `Deleted time ID ${t.id} from support call ID ${this.selectedCall.id}`) } catch (e) { }
        } catch (e) {
          this.notify('error', 'Error deleting time')
        }
      },

      deleteConfirm(call) {
        this.selectedCall = call
        const modal = this.$refs.deleteSupportModal
        if (modal && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const bsModal = bootstrap.Modal.getOrCreateInstance(modal)
          setTimeout(() => bsModal.show(), 150)
        }
      },
      async confirmDelete() {
        try {
          await axios.delete(`${API}/delete-supportcall/${this.selectedCall.id}`)
          this.notify('success', 'Support call deleted')
          try { await this.callLog(this.storedUser?.email || '--', 'delete', 'remoteCalls', `Deleted support call with ID ${this.selectedCall.id}`) } catch (e) { }
          const modal = this.$refs.deleteSupportModal
          if (modal && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
            const bsModal = bootstrap.Modal.getOrCreateInstance(modal)
            bsModal.hide()
          }
          await Promise.all([this.fetchCalls(this.currentPage), this.fetchTotals()])
        } catch (e) {
          this.notify('error', 'Error deleting support call')
        }
      }
    }
  }
</script>

<style>
  .fSmall {
    font-size: 10px !important;
  }

  .serial-number {
    cursor: pointer;
    color: #075cff;
    text-decoration: underline;
  }

  .hoverClick {
    cursor: pointer;
  }

  /* Improve spacing between titles and values in print layout */
  #print-area table { width: 100%; table-layout: fixed; }
  #print-area table th {
    width: 260px;
    vertical-align: top;
  }
  #print-area table td {
    padding-left: 16px;
    white-space: nowrap;
  }
  #print-area-report table th {
    width: 240px;
    vertical-align: top;
  }
  #print-area-report table td {
    padding-left: 16px;
    white-space: nowrap;
  }
  /* Wider preview tables */
  .report-table {
    table-layout: fixed;
    width: 100%;
  }
  .report-table th {
    width: 240px;
  }
</style>